<script setup lang="ts">
import type { Translate } from '@/plugins/i18n/types';
import { computed, inject } from 'vue';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { MixpanelInstance } from '@/plugins/mixpanel/mixpanel';
import { useGlobalStore } from '@/stores/global';

const globalStore = useGlobalStore();
const { isBrainDone, isDriveDone, isShapeDone } = useMeQuery();
const { auth } = useMeQuery();
const t = inject('t') as Translate;

const assessments = computed(() => ({
  SHAPE: isShapeDone.value,
  DRIVE: isDriveDone.value,
  BRAIN: isBrainDone.value,
}));

/**
 * @todo refactor this to be more simple
 */
const hasAllAssessments = computed(() => {
  return assessments.value.SHAPE && assessments.value.DRIVE && assessments.value.BRAIN;
});

const hasOnlyOneAssessment = computed(() => {
  return Object.values(assessments.value).filter(Boolean).length === 1;
});

const hasOneAssessmentLeft = computed(() => {
  return Object.values(assessments.value).filter(Boolean).length === 2;
});

const notPassedAssessments = computed(() => {
  return Object.keys(assessments.value).filter((key) => !assessments.value[key]);
});

const infoMessage = computed(() => {
  if (hasOnlyOneAssessment.value) {
    const [assessment_1, assessment_2] = notPassedAssessments.value;

    return {
      body: t('candidate_account.share.panel.report.info.one_assessment.body', {
        assessment_1,
        assessment_2,
      }),
      link: t('candidate_account.share.panel.report.info.one_assessment.link'),
    };
  }

  if (hasOneAssessmentLeft.value) {
    const [assessment] = notPassedAssessments.value;

    return {
      body: t('candidate_account.share.panel.report.info.two_assessments.body', {
        assessment,
      }),
      link: t('candidate_account.share.panel.report.info.two_assessments.link'),
    };
  }

  return {
    body: t('candidate_account.share.panel.report.info.no_assessment.body'),
    link: t('candidate_account.share.panel.report.info.no_assessment.link'),
  };
});

function trackMixpanel() {
  MixpanelInstance.track('share-profile_assessment', {
    user_uuid: auth.value?.uuid,
    email: auth.value?.email,
    assessment_todo: notPassedAssessments.value.join(','),
  });
}
</script>

<template>
  <div class="flex-auto flex flex-col gap-6 p-6">
    <svg aria-hidden="true" focusable="false" class="absolute w-0 h-0">
      <defs>
        <linearGradient id="gradient">
          <stop offset="0%" stop-color="#0096FF" />
          <stop offset="21.84%" stop-color="#029CAF" />
          <stop offset="46.85%" stop-color="#F59F00" />
          <stop offset="71.44%" stop-color="#F06F38" />
          <stop offset="100%" stop-color="#FF2F92" />
        </linearGradient>
      </defs>
    </svg>

    <article v-if="!hasAllAssessments" class="p-4 bg-info-background flex gap-3 text-body-3">
      <v-icon size="1rem" icon="fa-circle-info" color="blue-darken-1" />
      <p class="text-sm">
        {{ infoMessage.body }}
        <a
          class="font-medium underline text-af-blue-600"
          :href="globalStore.assessments_url"
          @click="trackMixpanel"
        >
          {{ infoMessage.link }}
        </a>
      </p>
    </article>

    <section class="grid gap-3">
      <h2 class="text-af-grey-700 font-extrabold text-base font-heading">
        {{ $t('candidate_account.share.panel.report.title') }}
      </h2>
      <p class="text-body-3 font-light">
        {{ $t('candidate_account.share.panel.report.body') }}
      </p>
    </section>

    <section class="grid gap-5">
      <article class="flex gap-3">
        <VIcon icon="fat fa-clipboard-question" class="fill-gradient" size="16px" />
        <div>
          <h3 class="text-af-grey-700 text-sm font-bold leading-tight">
            {{ $t('candidate_account.share.panel.report.tip_1.title') }}
          </h3>
          <p class="text-body-3">{{ $t('candidate_account.share.panel.report.tip_1.body') }}</p>
        </div>
      </article>

      <article class="flex gap-3">
        <VIcon icon="fat fa-share-nodes" class="fill-gradient" size="16px" />
        <div>
          <h3 class="text-af-grey-700 text-sm font-bold leading-tight">
            {{ $t('candidate_account.share.panel.report.tip_2.title') }}
          </h3>
          <p class="text-body-3">{{ $t('candidate_account.share.panel.report.tip_2.body') }}</p>
        </div>
      </article>

      <article class="flex gap-3">
        <VIcon icon="fat fa-person-arrow-up-from-line" class="fill-gradient" size="16px" />
        <div>
          <h3 class="text-af-grey-700 text-sm font-bold leading-tight">
            {{ $t('candidate_account.share.panel.report.tip_3.title') }}
          </h3>
          <p class="text-body-3">{{ $t('candidate_account.share.panel.report.tip_3.body') }}</p>
        </div>
      </article>
    </section>
  </div>
</template>

<style scoped>
:deep(.fill-gradient > svg > path) {
  fill: url(#gradient);
}
</style>
